import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { ProgressSpinner } from "primereact/progressspinner";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';
import config from "../config";
import { refusalMessage, mayEnter } from "../utils/loginRefusal";
import { clearPermissionsCache } from "../hooks/usePermissions";
import { clearSessionState } from "../utils/logout";

const SignIn = ({ onLogin }) => {
  let navigate = useNavigate ();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("error");
  const [organization, setOrganization] = useState("");
  const [companyid, setCompanyId] = useState("");

  // Load saved credentials on component mount
  useEffect(() => {
    const savedUsername = localStorage.getItem('savedUsername');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';

    // Remember the username only; the password is never stored.
    if (savedUsername && savedRememberMe) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  async function getOrganization() {
    try {
      var myHeaderss = new Headers();
      const username = localStorage.getItem('userName');
      myHeaderss.append("username", username);
      var requestOptions = {
        method: 'GET',
        headers: myHeaderss,
        redirect: 'follow'
      };
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}organization-name`,requestOptions);
      if (response.status === 200) {
        const responseJson = await response.json();
        if(responseJson.success == false){
          setOrganization("");
        }
        else if(responseJson.status == 400){
          setOrganization("");
        }
        else{
          localStorage.setItem('organization', responseJson.data.name);
          setOrganization(responseJson.data.name);
          localStorage.setItem('features', responseJson.data.enabledFeatures);
         
        }
      }
      else{
        setOrganization("");
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function getCompanyId() {
    try {
      var myHeaderss = new Headers();
      const username = localStorage.getItem('userName');
      myHeaderss.append("username", username);
      var requestOptions = {
        method: 'GET',
        headers: myHeaderss,
        redirect: 'follow'
      };
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}getcompanyid`,requestOptions);
      if (response.status === 200) {
        const responseJson = await response.json();
        if(responseJson.success == false){
          setCompanyId("");
        }
        else if(responseJson.status == 400){
          setCompanyId("");
        }
        else{
          localStorage.setItem('companyid', responseJson.data);
          setCompanyId(responseJson.data);
        }
      }
      else{
        setCompanyId("");
      }
    } catch (error) {
      console.error(error);
    }
  }

  // Same provisioning gate the Microsoft path applies: a valid password proves
  // who you are, not that an organisation on EcoPrism has you. Without this a
  // deprovisioned password account still reached an empty app.
  async function getLoginState(email) {
    const headers = new Headers();
    headers.append("username", email);
    const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}my-permissions`, {
      method: "GET",
      headers,
      redirect: "follow",
    });
    if (response.status === 200) return response.json();
    const err = new Error(`Sign-in check failed (${response.status})`);
    err.status = response.status;
    throw err;
  }

  const clearSession = () => {
    clearSessionState();
    clearPermissionsCache();
  };

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (e.target.value.trim()) {
      setErrors((prev) => ({ ...prev, username: "" }));
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (e.target.value.trim()) {
      setErrors((prev) => ({ ...prev, password: "" }));
    }
  };

  const handleRememberMeChange = (e) => {
    setRememberMe(e.checked);
    // If unchecking remember me, clear saved credentials
    if (!e.checked) {
      localStorage.removeItem('savedUsername');
      localStorage.removeItem('savedPassword');
      localStorage.removeItem('rememberMe');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ username: "", password: "" });
    setStatusMessage("");
    setLoading(true); // Show loader

    let hasError = false;
    if (!username.trim()) {
      setErrors((prev) => ({ ...prev, username: "E-mail is required" }));
      hasError = true;
    }
    if (!password.trim()) {
      setErrors((prev) => ({ ...prev, password: "Password is required" }));
      hasError = true;
    }

    if (hasError) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}auth/login`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify({ username, password }),
      });

      // Guard non-JSON responses (e.g. HTML page during backend restart)
      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        throw new Error("Login service is temporarily unavailable. Please try again in a moment.");
      }

      if (!res.ok) {
        throw new Error(data?.error || "Login failed");
      }
      if (data?.data?.id_token) {
        // Save credentials if "Remember me" is checked
        if (rememberMe) {
          localStorage.setItem('savedUsername', username);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('savedUsername');
          localStorage.removeItem('rememberMe');
        }
        // Never store the password; purge any left by older builds.
        localStorage.removeItem('savedPassword');
        localStorage.removeItem('oldp');

        // Start from a clean slate so nothing from a previous account leaks in.
        clearSession();
        localStorage.setItem('userName', username);
        localStorage.setItem('loginType', 'Auth0');
        localStorage.setItem('JWT',  data.data.id_token);
        const decodedToken = jwtDecode(data.data.id_token);
        localStorage.setItem('companyName', decodedToken.name);

        let state;
        try {
          state = await getLoginState(username);
        } catch (e) {
          clearSession();
          throw new Error("We couldn't verify your account right now. Please try again in a moment.");
        }
        if (!mayEnter(state)) {
          clearSession();
          throw new Error(refusalMessage(state.reason, { supportEmail: config.supportEmail }));
        }

        setStatusType("success");
        setStatusMessage("Login successful!");
        await getOrganization();
        await getCompanyId();
        const redirect = localStorage.getItem("redirectAfterLogin");
        localStorage.removeItem("redirectAfterLogin");
        const safeRedirect = (redirect && /^\/(?!\/)/.test(redirect)) ? redirect : '/home';
        navigate(safeRedirect);
      } else {
        // 200 without a token means the login service misbehaved — never
        // report success without one.
        throw new Error("Sign-in didn't complete. Please try again.");
      }
      //onLogin(username, password, rememberMe);
    } catch (error) {
      console.error("Error:", error.message);
      setStatusType("error");
      // fetch rejects with a TypeError ("Failed to fetch") when the server is
      // unreachable — translate the browser's raw message into something actionable.
      setStatusMessage(
        error instanceof TypeError
          ? "We couldn't reach the EcoPrism server. Please check your connection and try again in a moment."
          : error.message
      );
    } finally {
      setLoading(false); // Hide loader
    }
  };

  return (
    <form onSubmit={handleSubmit} className="sign-in-form">
      {/* E-mail Field */}
      <div className="form-group">
        <label className="formlable" htmlFor="username">
          Username
        </label>
        <InputText
          id="username"
          value={username}
          onChange={handleUsernameChange}
          autoComplete="username"
          className={`wd-100 form-control ${errors.username ? "is-invalid" : ""}`}
          placeholder="Type your username"
        />
        {errors.username && <div className="invalid-feedback">{errors.username}</div>}
      </div>

      {/* Password Field */}
      <div className="form-group">
        <label className="formlable" htmlFor="password">
          Password
        </label>
        <InputText
          id="password"
          type="password"
          value={password}
          onChange={handlePasswordChange}
          autoComplete="current-password"
          className={`wd-100 form-control ${errors.password ? "is-invalid" : ""}`}
          placeholder="Type your password"
        />
        {errors.password && <div className="invalid-feedback">{errors.password}</div>}
      </div>

      {/* Remember Me & Forgot Password. Explicit flex styles: the Bootstrap
          utility classes (d-flex/justify-content-between/ml-2) are not in this
          app's CSS, which left the two texts colliding into one jumbled line. */}
      <div
        className="form-footer"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px 16px", marginTop: "10px" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Checkbox inputId="remember" checked={rememberMe} onChange={handleRememberMeChange} />
          <label htmlFor="remember" className="formlable" style={{ marginBottom: 0, whiteSpace: "nowrap", cursor: "pointer" }}>
            Remember me
          </label>
        </div>
        <span className="formlable" style={{ whiteSpace: "nowrap" }}>
          Forgot password?{" "}
          <a href={`mailto:${config.supportEmail}`} title={config.supportEmail} style={{ color: "#407775", fontWeight: 600 }}>
            Contact support
          </a>
        </span>
      </div>

      {/* Loader and Submit Button */}
      <div className="flex justify-content-center mt-4">
        {loading ? (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : (
          <Button
            type="submit"
            className="next-button wd-100"
            style={{ justifyContent: "center", width: "100%" }}
            variant="contained"
            size="medium"
          >
            Sign in
          </Button>
        )}
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div
          className={`alert ${statusType === "success" ? "alert-success" : "alert-danger"} mt-3 mb-0`}
          role="alert"
        >
          {statusMessage}
        </div>
      )}
    </form>
  );
};

export default SignIn;